'use client';

import { useTranslations } from 'next-intl';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { BaseFillSubmit } from '@/app/components/icons/BaseFillSubmit';
import { Navs } from '@/app/components/shared/Nav';
import { POSTS_TYPE } from '@/app/components/shared/PostsModal/constants';
import { Button } from '@/app/components/ui/button';
import { SITE } from '@/app/constants';
import { useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { ExploreTabRouterEnum, HomeTabRouterEnum } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';
import { useExploreTabs } from '../_hooks/use-explore-tabs';
import { resolveHomePostType } from '../_utils';
import { HomeGenerationFormActionsProvider } from './HomeGenerationFormActionsContext';
import { HomeHero } from './HomeHero';
import { HomeToolsSection } from './HomeToolsSection';

interface HomeScrollContextValue {
  scrollToTop?: () => void;
}

const HomeScrollContext = createContext<HomeScrollContextValue>({});

export const useHomeScroll = () => {
  return useContext(HomeScrollContext);
};

interface HomeLayoutClientProps {
  children: React.ReactNode;
}

const POSTS_TYPE_MAP: Record<string, POSTS_TYPE | undefined> = {
  '/home/recommend': POSTS_TYPE.INSPIRATION,
  '/home/inspiration': POSTS_TYPE.INSPIRATION,
  '/home/short-film': POSTS_TYPE.SHORT_FILM,
  '/home/tutorial': POSTS_TYPE.TUTORIAL,
  '/home/reference': POSTS_TYPE.REFERENCE,
  '/home/activity': undefined,
};

const getHomeTabPath = (key: HomeTabRouterEnum | ExploreTabRouterEnum) => {
  switch (key) {
    case HomeTabRouterEnum.Recommend:
      return '/home/recommend';
    case ExploreTabRouterEnum.PostReferences:
      return '/home/reference';
    case ExploreTabRouterEnum.ShortFilm:
      return '/home/short-film';
    case ExploreTabRouterEnum.Tutorial:
      return '/home/tutorial';
    case HomeTabRouterEnum.Activity:
      return '/home/activity';
    case ExploreTabRouterEnum.Cases:
      return '/home/cases';
    default:
      return '/home/recommend';
  }
};

export const HomeLayoutClient = ({ children }: HomeLayoutClientProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const tabsRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const previousPathnameRef = useRef(pathname);
  const t = useTranslations();
  const { userInfo } = useStore((state) => ({
    userInfo: state.userInfo,
  }));
  const { open: openPostsModal } = useGlobalModal('postsModal');

  const handlePosts = () => {
    const mappedPostType = POSTS_TYPE_MAP[pathname] || POSTS_TYPE.INSPIRATION;
    const postType = resolveHomePostType(SITE, mappedPostType);
    if (pathname === '/home/activity') {
      openPostsModal({
        hidePostTutorial: true,
        hidePostReference: true,
      });
    } else {
      openPostsModal({
        postType,
      });
    }
    reportEvent('submit_click', {
      submit_position: 'home-recommend-submit',
    });
  };

  const scrollToTop = () => {
    const scrollEle = document.getElementById('scrollableDiv');
    if (scrollEle && tabsRef.current && listRef.current) {
      const scrollContainerTop = scrollEle.getBoundingClientRect().top;
      const listTop = listRef.current.getBoundingClientRect().top;
      scrollEle.scrollTop += listTop - scrollContainerTop - tabsRef.current.offsetHeight;
    }
  };
  const homeTabs = useExploreTabs(false);
  const providersValue = useMemo(() => ({ scrollToTop }), []);

  useEffect(() => {
    const scrollEle = document.getElementById('scrollableDiv');
    if (scrollEle) {
      scrollEle.scrollTop = 0;
    }
  }, []);

  useEffect(() => {
    if (previousPathnameRef.current !== pathname) {
      previousPathnameRef.current = pathname;
      scrollToTop();
    }
  }, [pathname]);

  const activeTab = useMemo<ExploreTabRouterEnum | HomeTabRouterEnum>(() => {
    const tab = pathname.split('/home/')[1]?.split('/')[0];
    if (
      SITE === 'china' &&
      (tab === ExploreTabRouterEnum.Inspiration ||
        tab === ExploreTabRouterEnum.ShortFilm ||
        tab === ExploreTabRouterEnum.Tutorial)
    ) {
      return HomeTabRouterEnum.Recommend;
    }
    return (tab as ExploreTabRouterEnum | HomeTabRouterEnum) || HomeTabRouterEnum.Recommend;
  }, [pathname]);

  return (
    <HomeGenerationFormActionsProvider>
      <div className="max-content relative">
        <div className="relative">
          <HomeHero />
          <div className="relative z-10">
            <HomeToolsSection />
          </div>
        </div>

        <div ref={tabsRef} className="sticky top-0 z-20 mt-6 pb-2">
          <div className="relative flex items-center justify-between bg-system-bg08">
            <Navs
              className="h-9 flex-1 pr-14"
              itemClassName="text-sm sm:text-xs"
              navs={homeTabs}
              active={activeTab}
              getHref={(item) => getHomeTabPath(item.key)}
              onChange={(item) => router.push(getHomeTabPath(item.key), { scroll: false })}
            />
            <div className="flex items-center gap-2">
              {userInfo && (
                <>
                  <button
                    type="button"
                    className="inline-flex h-9 items-center justify-center rounded-12 border border-system-white12 bg-system-white08 px-5 text-sm transition-colors hover:bg-system-white12 md:hidden"
                    onClick={handlePosts}
                  >
                    <BaseFillSubmit className="mr-1.5 text-[18px] text-system-white80" />
                    <span className="text-white text-xs">{t('home.submit')}</span>
                  </button>

                  {/* 移动端提交按钮 */}
                  <Button
                    variant="outline"
                    size="icon"
                    className="fixed right-4 bottom-4 z-50 hidden h-[56px] w-[56px] items-center justify-center rounded-full bg-ShengshuButton md:flex"
                    onClick={handlePosts}
                  >
                    <BaseFillSubmit className="text-[24px] text-black" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        <HomeScrollContext.Provider value={providersValue}>
          <div ref={listRef}>{children}</div>
        </HomeScrollContext.Provider>
      </div>
    </HomeGenerationFormActionsProvider>
  );
};
